import { buildMediaProxyUrl } from '@/features/Reports/media-proxy';
import { ResolvedMedia } from '@/features/Reports/media-resolution/types';

import {
  OpenVideoLink,
  PreviewEmbed,
  PreviewFallback,
  PreviewImage,
  PreviewVideo,
} from './MediaPreviewModal.styles';

type MediaPlayerProps = {
  media: ResolvedMedia;
  alt: string;
  sourceUrl?: string;
};

const IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';

export const MediaPlayer = ({ media, alt, sourceUrl }: MediaPlayerProps) => {
  if (media.mediaType !== 'video') {
    return null;
  }

  switch (media.playerType) {
    case 'iframe':
    case 'embed':
      if (media.embedUrl) {
        return (
          <PreviewEmbed
            src={media.embedUrl}
            title={media.title ?? alt}
            allow={IFRAME_ALLOW}
            allowFullScreen
            loading="lazy"
          />
        );
      }

      break;
    case 'html5':
    default:
      if (media.playbackUrl) {
        return (
          <PreviewVideo
            key={media.playbackUrl}
            src={media.playbackUrl}
            controls
            playsInline
            preload="metadata"
            poster={buildMediaProxyUrl(media.thumbnailUrl) ?? undefined}
          />
        );
      }

      break;
  }

  const posterSrc = buildMediaProxyUrl(media.thumbnailUrl);

  return (
    <PreviewFallback>
      {posterSrc && (
        <PreviewImage
          src={posterSrc}
          alt={alt}
        />
      )}
      {sourceUrl && (
        <OpenVideoLink
          href={sourceUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          Open media source
        </OpenVideoLink>
      )}
    </PreviewFallback>
  );
};
